﻿import { emailAddressesChangesConfirm, emailAddressesChangesRequest, EmailAddressesChangesRequestData, EmailAddressesChangesRequestDataRegion, EmailAddressesUpdatesResponse, getEmailAddressesMultiRegions, MultiRegionsEmailsListResponse, MultiRegionsEmailsListResponseItem } from "../../API/AutoNotifications/AutoNotifications";
import { RESTError } from "../../API/RESTError";
import { IHashData } from "../../IHashData";
import { IPage } from "../../IPage";
import { MainSite } from "../../MainSite";
import { SimpleDialog } from "../../SimpleDialog";

interface Block {
    emailContainer: JQuery;
    heading: JQuery;
    warning: JQuery;
    regionId: string;
    currentEmails: string[];
    newEmailAddresses: string[];
    invalidEmailAddresses: string[];
}

export class AutoNotificationsPage implements IPage {
    constructor(private mainSite: MainSite) {
    }

    public id: string = 'autoNotifications';

    private navBarLink: JQuery;

    public initialize(): void {
    }

    private container: JQuery = null;
    private blocks: Block[] = null;
    private $saveButton: JQuery = null;
    
    public handleHashChange(hashData: IHashData): boolean {
        if (hashData.Page === this.id) {
            if (!this.container) {
                this.getData();
            }
            return true;
        } else {
            if (this.container) {
                this.container.remove();
                this.container = null;
            }
            return false;
        }
    }

    private topRender() {
        if (this.container) {
            this.container.remove();
            this.container = null;
        }
        this.container = $('<div class="container"></div>').appendTo('body');

        $('<h1>Automatic Notifications</h1>').appendTo(this.container);

        $('<p>The email addresses listed below will receive automatic notifications in the event of a sensor drop out, for the corresponding region.</p>')
            .appendTo(this.container);

        $('<p>Email addresses should be separated by a semi-colon (<strong>;</strong>). Click the <strong><i class="fa fa-floppy-o"></i> Save Changes</strong> button when you have finished editing.</p>')
            .appendTo(this.container);

        this.$saveButton = $('<button type="button" class="btn btn-success midi_autoNotificationsPage_saveChangesButton"><i class="fa fa-floppy-o"></i> Save Changes</button>')
            .prop('disabled', true)
            .appendTo(this.container)
            .click(() => {
                let valid = true;
                for (let i = 0; i < this.blocks.length; i++) {
                    this.validate(this.blocks[i]);
                    if (this.blocks[i].invalidEmailAddresses.length) {
                        this.blocks[i].warning
                            .text("The following email addresses are invalid: " + this.blocks[i].invalidEmailAddresses.join("; "))
                            .show();

                        this.blocks[i].emailContainer.addClass("has-error");
                        valid = false;
                    } else {
                        this.blocks[i].emailContainer.removeClass("has-error");
                        this.blocks[i].warning.hide();
                    }
                };

                if (valid) {
                    this.modifyEmailAddresses();
                }
            });
    }

    private getData(): void {
        this.mainSite.pageBlock("Retrieving data...");
        this.blocks = [];
        let regionsIds: string[] = [];

        this.topRender();

        for (let key in this.mainSite.staticDataManager.Schema.RegionSet.Regions) {
            regionsIds.push(key);
            this.blocks.push({
                regionId: key,
                currentEmails: [],
                invalidEmailAddresses: [],
                newEmailAddresses: [],
                heading: $('<h3></h3>').text(this.mainSite.staticDataManager.Schema.RegionSet.Regions[key].Label),
                emailContainer: $('<textarea rows="8" cols="80" class="midi_autoNotificationsPage_box" placeholder="Enter email addresses here"></textarea>'),
                warning: $('<div class="alert alert-danger"></div>').hide()
            });
        }

        this.blocks.sort((a, b) => {
            if (a.regionId < b.regionId)
                return -1;
            if (a.regionId > b.regionId)
                return 1;
            return 0;
        });
        regionsIds.sort((a, b) => {
            if (a < b)
                return -1;
            if (a > b)
                return 1;
            return 0;
        });

        getEmailAddressesMultiRegions(regionsIds, (err, data: MultiRegionsEmailsListResponse) => {
            if (err) {
                new SimpleDialog("Server Error", "Unable to retrieve data for this page: " + err.Message).show();
                this.mainSite.pageUnblock();
                return;
            }

            this.$saveButton.prop('disabled', false);

            let ii: number = 0;
            for (let regionId in data.Data) {
                let item: MultiRegionsEmailsListResponseItem = data.Data[regionId];
                this.blocks.filter(x => x.regionId == regionId).forEach((block: Block) => {
                    block.currentEmails = item.EmailRecipientsList;

                    let emailsText = block.currentEmails ? block.currentEmails.join("; ") : "";
                    block.heading.appendTo(this.container);
                    block.warning.appendTo(this.container);
                    block.emailContainer.appendTo(this.container).val(emailsText);

                    ii++;

                    if (ii == this.blocks.length - 1) {
                        this.mainSite.pageUnblock();
                    }
                });
            }
        });
    }

    private modifyEmailAddresses(): void {
        this.mainSite.pageBlock("Sending changes request to the server...");

        let data: EmailAddressesChangesRequestData = {
            Regions: []
        };

        for (let i = 0; i < this.blocks.length; i++) {
            let rd: EmailAddressesChangesRequestDataRegion = {
                RegionId: this.blocks[i].regionId,
                EmailAddresses: this.blocks[i].newEmailAddresses
            };

            data.Regions.push(rd);
        }

        emailAddressesChangesRequest(data, ((err: RESTError, data: EmailAddressesUpdatesResponse) => {
            if (err) {
                this.blocks[0].warning.text("Error updating email addresses: " + err.Message);
                this.blocks[0].warning.show();
                this.mainSite.pageUnblock();
                return;
            }

            this.blocks[0].warning.hide();

            let confirmRemovesCount: number = 0;
            let regionsGroupedMessages: string[] = [];

            data.Regions.forEach(region => {
                let regionMessages: string[] = [];

                if (region.EmailsRemoved.length > 0) {
                    region.EmailsRemoved.forEach(email => {
                        regionMessages.push(`<i class="fa fa-fw fa-lg fa-minus-square text-danger"></i> <span class="label label-danger">REMOVE</span> ${email}`);
                        confirmRemovesCount++;
                    });
                }

                if (region.EmailsAdded.length > 0) {
                    region.EmailsAdded.forEach(email => {
                        regionMessages.push(`<i class="fa fa-fw fa-lg fa-plus text-success"></i> <span class="label label-success">ADD</span> ${email}`);
                    });
                }

                if (regionMessages.length > 0) {
                    regionsGroupedMessages.push(`<strong>${region.RegionLabel}:</strong><br>${regionMessages.join('<br>')}`);
                }
            });

            if (regionsGroupedMessages.length > 0) {
                let _html: string = "<p><em>You are about to make the following modifications. <strong>Please confirm this is correct.</strong></em></p>";
                if (confirmRemovesCount > 0) {
                    _html += "<p class=\"text-muted\">Note: each removed email will be sent a notification that they were removed from the list of recipients.</p>";
                }
                _html += `<p>${regionsGroupedMessages.join('</p><p>')}</p>`;

                let sd: SimpleDialog = new SimpleDialog("Confirm changes", _html, true, true, _sd => {
                    this.mainSite.pageBlock("Sending confirmation of changes to the server...");

                    emailAddressesChangesConfirm(data, ((err: RESTError, data: EmailAddressesUpdatesResponse) => {
                        if (err) {
                            this.blocks[0].warning.text("Error updating email addresses: " + err.Message);
                            this.blocks[0].warning.show();
                            this.mainSite.pageUnblock();
                            return;
                        }

                        this.blocks[0].warning.hide();
                        this.mainSite.pageUnblock();
                        new SimpleDialog("Success", "You have successfully updated the automatic notification settings.").show();
                    }));

                    _sd.hide();
                });

                this.mainSite.pageUnblock();
                sd.show();
            }
        }));
    }

    private validate(block: Block) {
        let emailAddresses: string[] = block.emailContainer.val().split(";");
        block.newEmailAddresses = [];
        block.invalidEmailAddresses = [];

        for (let i = 0; i < emailAddresses.length; i++) {
            emailAddresses[i] = emailAddresses[i].trim();

            if (emailAddresses[i] !== "") {
                let emailValid = this.validateEmailAddress(emailAddresses[i]);
                if (!emailValid && emailAddresses[i]) {
                    block.invalidEmailAddresses.push(emailAddresses[i]);
                } else {
                    block.newEmailAddresses.push(emailAddresses[i]);
                }
            }
        }
    }

    private validateEmailAddress(s: string): boolean {
        let re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(s);
    }
}